import React, { useState, useEffect } from 'react';
import { TourPosition } from './types';

interface TourOverlayProps {
  targetElement: HTMLElement;
  allowInteraction?: boolean;
}

const TourOverlay: React.FC<TourOverlayProps> = ({
  targetElement,
  allowInteraction = false,
}) => {
  const [position, setPosition] = useState<TourPosition>({ top: 0, left: 0, width: 0, height: 0 });

  useEffect(() => {
    if (!targetElement) return;

    // Store original z-index and position
    const originalZIndex = targetElement.style.zIndex;
    const originalPosition = targetElement.style.position;

    // Raise z-index of target element to be above overlay
    targetElement.style.zIndex = '9999';
    if (!originalPosition || originalPosition === 'static') {
      targetElement.style.position = 'relative';
    }

    const updatePosition = () => {
      const rect = targetElement.getBoundingClientRect();
      const padding = 8; // Default spotlight padding

      setPosition({
        top: rect.top - padding,
        left: rect.left - padding,
        width: rect.width + (padding * 2),
        height: rect.height + (padding * 2),
      });
    };

    // Initial position calculation
    updatePosition();

    // Update position on scroll or resize
    const handleUpdate = () => {
      requestAnimationFrame(updatePosition);
    };

    window.addEventListener('scroll', handleUpdate, true);
    window.addEventListener('resize', handleUpdate);

    return () => {
      // Restore original z-index and position
      targetElement.style.zIndex = originalZIndex;
      if (!originalPosition || originalPosition === 'static') {
        targetElement.style.position = originalPosition;
      }

      window.removeEventListener('scroll', handleUpdate, true);
      window.removeEventListener('resize', handleUpdate);
    };
  }, [targetElement]);

  const handleOverlayClick = (event: React.MouseEvent) => {
    // Allow clicks on the highlighted element if interaction is enabled
    const { clientX, clientY } = event;
    const { top, left, width, height } = position;

    const clickedInsideSpotlight =
      clientX >= left &&
      clientX <= left + width &&
      clientY >= top &&
      clientY <= top + height;

    if (clickedInsideSpotlight && allowInteraction) {
      // Allow the click to pass through to the highlighted element
      return;
    }

    // Prevent clicks outside the spotlight
    event.preventDefault();
    event.stopPropagation();
  };

  const blurStyle = {
    backdropFilter: 'blur(4px)',
    WebkitBackdropFilter: 'blur(4px)',
    backgroundColor: 'rgba(0, 0, 0, 0.5)',
    position: 'fixed' as const,
    zIndex: 9998,
    pointerEvents: 'none' as const,
  };

  return (
    <>
      {/* Top blur bar */}
      <div
        style={{
          ...blurStyle,
          top: 0,
          left: 0,
          right: 0,
          height: Math.max(0, position.top),
        }}
      />

      {/* Left blur bar */}
      <div
        style={{
          ...blurStyle,
          top: position.top,
          left: 0,
          width: Math.max(0, position.left),
          height: position.height,
        }}
      />

      {/* Right blur bar */}
      <div
        style={{
          ...blurStyle,
          top: position.top,
          left: position.left + position.width,
          right: 0,
          height: position.height,
        }}
      />

      {/* Bottom blur bar */}
      <div
        style={{
          ...blurStyle,
          top: position.top + position.height,
          left: 0,
          right: 0,
          bottom: 0,
        }}
      />

      {/* Clickable overlay */}
      <div
        className="tour-overlay"
        onClick={handleOverlayClick}
        role="presentation"
        aria-hidden="true"
      >
        {/* Spotlight border and glow */}
        <div
          className="tour-spotlight"
          style={{
            position: 'absolute',
            top: position.top,
            left: position.left,
            width: position.width,
            height: position.height,
          }}
        />
      </div>
    </>
  );
};

export default TourOverlay;